import { makeAutoObservable, runInAction } from "mobx";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  deleteSwayyProduct,
  isCancelledSwayyRequest,
  listSwayyBrands,
  listSwayyCategories,
  listSwayyProducts,
  normalizeSwayyApiError,
  SwayyAdminProductApi,
  SwayyApiError,
  SwayyCategoryApi,
  updateSwayyProductStatus,
} from "../api/SwayyProductsAPI";
import {
  SwayyProduct,
  SwayyProductFilters,
  SwayyProductListParams,
  SwayyProductsLoadState,
  SwayyProductSort,
  SwayyProductStatus,
  SwayySalesChannel,
} from "../models/products/SwayyProduct";

const DEFAULT_FILTERS: SwayyProductFilters = {
  search: "",
  status: "live",
  brandId: null,
  categoryId: null,
  channels: [],
  sortBy: "newest",
};

export class SwayyProductsStore {
  products: SwayyProduct[] = [];
  filters: SwayyProductFilters = { ...DEFAULT_FILTERS };
  searchDraft = "";
  brandOptions: FilterValue[] = [];
  categoryOptions: FilterValue[] = [];
  loadState: SwayyProductsLoadState = "idle";
  isRefreshing = false;
  isLoadingFilterOptions = false;
  isUpdating = false;
  errorMessage = "";
  errorStatus?: number;
  totalResults = 0;
  offset = 0;
  pageSize = 30;
  private productsAbortController?: AbortController;
  private optionsAbortController?: AbortController;
  private requestSequence = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalResults / this.pageSize));
  }

  get page(): number {
    return Math.floor(this.offset / this.pageSize) + 1;
  }

  get rangeStart(): number {
    return this.totalResults === 0 ? 0 : this.offset + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.offset + this.products.length, this.totalResults);
  }

  get hasActiveFilters(): boolean {
    return (
      Boolean(this.filters.search) ||
      this.filters.status !== DEFAULT_FILTERS.status ||
      this.filters.brandId !== null ||
      this.filters.categoryId !== null ||
      this.filters.channels.length > 0 ||
      this.filters.sortBy !== DEFAULT_FILTERS.sortBy
    );
  }

  get listQueryParams(): SwayyProductListParams {
    const trimmedSearch = this.filters.search.trim();
    return {
      ...(trimmedSearch && { q: trimmedSearch }),
      status: this.filters.status,
      ...(this.filters.brandId !== null && {
        brand_id: this.filters.brandId,
      }),
      ...(this.filters.categoryId !== null && {
        category_id: this.filters.categoryId,
      }),
      ...(this.filters.channels.includes("B2C") && { is_b2c: true as const }),
      ...(this.filters.channels.includes("B2B") && { is_b2b: true as const }),
      sort: this.filters.sortBy,
      limit: this.pageSize,
      offset: this.offset,
    };
  }

  async loadProducts(): Promise<SwayyApiError | null> {
    this.productsAbortController?.abort();
    const controller = new AbortController();
    this.productsAbortController = controller;
    const requestId = ++this.requestSequence;
    const isInitialRequest = this.loadState === "idle" && this.products.length === 0;

    if (isInitialRequest) {
      this.loadState = "loading";
    } else {
      this.isRefreshing = true;
    }
    this.errorMessage = "";
    this.errorStatus = undefined;

    try {
      const response = await listSwayyProducts(
        this.listQueryParams,
        controller.signal,
      );
      if (requestId !== this.requestSequence) return null;

      runInAction(() => {
        this.products = response.data.map(this.mapProduct);
        this.totalResults = response.pagination.total;
        this.offset = response.pagination.offset;
        this.pageSize = response.pagination.limit;
        this.loadState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error) || requestId !== this.requestSequence) {
        return null;
      }
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load products. Please try again.",
      );
      runInAction(() => {
        this.loadState = apiError.status === 403 ? "forbidden" : "error";
        this.errorMessage =
          apiError.status === 403
            ? "You don’t have permission to view products."
            : apiError.message;
        this.errorStatus = apiError.status;
      });
      return apiError;
    } finally {
      if (requestId === this.requestSequence) {
        runInAction(() => {
          this.isRefreshing = false;
        });
      }
    }
  }

  async loadFilterOptions(): Promise<SwayyApiError | null> {
    this.optionsAbortController?.abort();
    const controller = new AbortController();
    this.optionsAbortController = controller;
    this.isLoadingFilterOptions = true;

    try {
      const [brands, categories] = await Promise.all([
        listSwayyBrands(controller.signal),
        listSwayyCategories(controller.signal),
      ]);
      runInAction(() => {
        this.brandOptions = brands.map((brand) => ({
          id: String(brand.id),
          value: brand.name,
        }));
        this.categoryOptions = this.flattenCategories(categories);
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      return normalizeSwayyApiError(
        error,
        "Unable to load product filter options.",
      );
    } finally {
      runInAction(() => {
        this.isLoadingFilterOptions = false;
      });
    }
  }

  applyUrlParams(
    filters: SwayyProductFilters,
    pageSize: number,
    offset: number,
  ): void {
    this.filters = filters;
    this.searchDraft = filters.search;
    this.pageSize = pageSize;
    this.offset = offset;
  }

  setSearchDraft(value: string): void {
    this.searchDraft = value;
  }

  setSearch(search: string): void {
    if (this.filters.search === search) return;
    this.filters.search = search;
    this.resetOffset();
  }

  setStatus(status: SwayyProductStatus): void {
    if (this.filters.status === status) return;
    this.filters.status = status;
    this.resetOffset();
  }

  setBrandId(brandId: number | null): void {
    if (this.filters.brandId === brandId) return;
    this.filters.brandId = brandId;
    this.resetOffset();
  }

  setCategoryId(categoryId: number | null): void {
    if (this.filters.categoryId === categoryId) return;
    this.filters.categoryId = categoryId;
    this.resetOffset();
  }

  setChannels(channels: SwayySalesChannel[]): void {
    if (
      this.filters.channels.length === channels.length &&
      this.filters.channels.every((channel) => channels.includes(channel))
    ) {
      return;
    }
    this.filters.channels = channels;
    this.resetOffset();
  }

  setSortBy(sortBy: SwayyProductSort): void {
    if (this.filters.sortBy === sortBy) return;
    this.filters.sortBy = sortBy;
    this.resetOffset();
  }

  setPage(page: number): void {
    const safePage = Math.min(Math.max(page, 1), this.totalPages);
    this.offset = (safePage - 1) * this.pageSize;
  }

  setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.resetOffset();
  }

  clearFilters(): void {
    this.filters = { ...DEFAULT_FILTERS };
    this.searchDraft = "";
    this.resetOffset();
  }

  async changeProductStatus(
    productId: number,
    status: SwayyProductStatus,
  ): Promise<SwayyApiError | null> {
    this.isUpdating = true;
    try {
      await updateSwayyProductStatus(productId, status);
      return null;
    } catch (error) {
      return normalizeSwayyApiError(
        error,
        "Unable to update the product status.",
      );
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  async deleteProduct(productId: number): Promise<SwayyApiError | null> {
    this.isUpdating = true;
    try {
      await deleteSwayyProduct(productId);
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to delete the product.");
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  dispose(): void {
    this.productsAbortController?.abort();
    this.optionsAbortController?.abort();
  }

  private resetOffset(): void {
    this.offset = 0;
  }

  private mapProduct(product: SwayyAdminProductApi): SwayyProduct {
    const channels: SwayySalesChannel[] = [];
    if (product.is_b2c) channels.push("B2C");
    if (product.is_b2b) channels.push("B2B");

    return {
      id: product.id,
      name: product.name,
      sku: product.sku || "—",
      slug: product.slug,
      thumbnail: product.thumbnail || undefined,
      brandName: product.brand_name || "—",
      status: product.status,
      channels,
      variantCount: product.variant_count,
      updatedBy: product.updated_admin?.name || "—",
      updatedAt: product.updated_at,
    };
  }

  private flattenCategories(
    categories: SwayyCategoryApi[],
    ancestors: string[] = [],
  ): FilterValue[] {
    return categories.flatMap((category) => {
      const labels = [...ancestors, category.name];
      return [
        { id: String(category.id), value: labels.join(" / ") },
        ...this.flattenCategories(category.children || [], labels),
      ];
    });
  }
}
